import { useState, type WheelEvent } from 'react';
import { fadeMask, useOverflowEdges } from '../ui/overflowEdges';
import type { Tone } from '../ui/Badge';
import { CREW_STATE_TONE, type CrewChip } from './crewPins';
import { Face } from './Face';

/**
 * The crew chips: "Everyone", then one per tech, in a strip along the bottom of the crew map
 * (ops insights Phase 4, ticket 03).
 *
 * Each chip shows the tech's face (initials, ringed in their STATE's tone, never a colour of
 * their own [Q3]), first name, state in words, and the server's `why` when the map cannot
 * draw them. Every tech has a chip, so a tech with no face (between jobs, not started, done,
 * or nowhere drawable) is still on screen.
 *
 * ⚠️ Buttons with `aria-pressed`, reachable by Tab with a visible focus ring; focusing one
 * scrolls the strip to it. A vertical wheel scrolls the strip sideways, since a mouse wheel
 * has no sideways of its own, and an edge that has more chips past it fades out, so a strip
 * cut off at a chip boundary still reads as one that scrolls. The pick is the PAGE's (ticket
 * 04's panel reads it): the chips arrive with it marked, and a press asks the page to change
 * it.
 */
export function CrewChips({
  chips,
  visits,
  onPick,
}: {
  chips: readonly CrewChip[];
  /** How many visits the day has: under "Everyone". A pick naming nobody shows as it. */
  visits: number;
  onPick: (userId: string | null) => void;
}) {
  const nobody = !chips.some((c) => c.picked);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const edges = useOverflowEdges(scroller, chips.length);
  const sideways = (e: WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) {
      el.scrollLeft += e.deltaY;
    }
  };
  return (
    <div
      data-probe="crew-chips"
      className="pointer-events-auto max-w-full rounded-[var(--radius)] border border-border bg-surface p-1.5 shadow-md"
    >
      <div
        ref={setScroller}
        role="group"
        aria-label="Crew"
        onWheel={sideways}
        data-fade-start={edges.start || undefined}
        data-fade-end={edges.end || undefined}
        style={{ maskImage: fadeMask(edges), WebkitMaskImage: fadeMask(edges) }}
        className="flex gap-1.5 overflow-x-auto"
      >
        <Chip
          pressed={nobody}
          onClick={() => onPick(null)}
          face="All"
          tone="neutral"
          name="Everyone"
          line={`${visits} ${visits === 1 ? 'visit' : 'visits'}`}
        />
        {chips.map((c) => (
          <Chip
            key={c.userId}
            userId={c.userId}
            pressed={c.picked}
            // Pressing the picked chip again lets go, as "Everyone" does.
            onClick={() => onPick(c.picked ? null : c.userId)}
            face={c.initials}
            tone={CREW_STATE_TONE[c.state]}
            name={c.firstName}
            title={c.name}
            line={c.stateLabel}
            why={c.why}
          />
        ))}
      </div>
    </div>
  );
}

function Chip({
  userId,
  pressed,
  onClick,
  face,
  tone,
  name,
  title,
  line,
  why,
}: {
  userId?: string;
  pressed: boolean;
  onClick: () => void;
  face: string;
  tone: Tone;
  name: string;
  title?: string;
  line: string;
  why?: string | null;
}) {
  return (
    <button
      type="button"
      data-crew-chip={userId ?? 'everyone'}
      aria-pressed={pressed}
      title={title}
      onClick={onClick}
      className="flex shrink-0 items-center gap-2 rounded-[var(--radius)] border border-transparent px-2 py-1 text-left hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand aria-pressed:border-border aria-pressed:bg-surface-2"
    >
      <Face text={face} tone={tone} />
      <span className="min-w-0 leading-tight">
        <span className="block text-xs font-semibold whitespace-nowrap">{name}</span>
        <span className="block text-[11px] whitespace-nowrap text-muted-foreground">
          {line}
        </span>
        {why ? (
          <span className="block max-w-56 text-[11px] text-[var(--sw-warn-text)]">
            {why}
          </span>
        ) : null}
      </span>
    </button>
  );
}
